import React from "react";
import { Heart, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { GoldBtn, OutlineBtn } from "@/components/Bits";
import { IMAGES, QUOTE } from "@/config/content";
import { useDonate } from "@/context/DonateContext";

export default function Legacy() {
  const { openDonate } = useDonate();
  const reduce = useReducedMotion();
  return (
    <div>
      <Seo title="The Legacy of Emperor Haile Selassie I" description="A tribute to His Imperial Majesty Emperor Haile Selassie I and his enduring commitment to education in Ethiopia." path="/legacy" />

      {/* Full-bleed cinematic hero */}
      <section className="relative grain min-h-[92vh] flex items-end overflow-hidden" data-testid="legacy-hero">
        <motion.img
          src={IMAGES.emperorDesk}
          alt="Emperor Haile Selassie I seated at his desk"
          className="absolute inset-0 h-full w-full object-cover object-top grayscale-[10%]"
          initial={reduce ? {} : { scale: 1.12 }}
          animate={reduce ? {} : { scale: 1 }}
          transition={{ duration: 16, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-20">
          <div className="kicker mb-5">His Imperial Majesty · A Tribute</div>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ivory max-w-4xl">
            The Emperor who invested in minds.
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-4xl px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <p className="font-serif text-2xl sm:text-3xl font-light leading-snug text-ivory2">
            Emperor Haile Selassie I devoted himself to the belief that the future of Ethiopia lay in the
            education of its people. His conviction — that knowledge, freely shared, could lift a nation —
            remains the guiding light of this Foundation.
          </p>
        </Reveal>
      </section>

      {/* Quote band */}
      <section className="relative grain overflow-hidden border-y border-gold/20 bg-ink2">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-24 sm:py-32 text-center">
          <Reveal>
            <div className="mx-auto mb-8 h-px w-16 bg-gold" />
            <blockquote className="font-serif text-3xl sm:text-5xl font-light italic leading-snug text-ivory">
              "{QUOTE.text}"
            </blockquote>
            <p className="mt-8 kicker">{QUOTE.attribution} · {QUOTE.year}</p>
          </Reveal>
        </div>
      </section>

      {/* Education imagery */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal className="relative">
          <div className="absolute -inset-3 border border-gold/20" aria-hidden="true" />
          <img src={IMAGES.graduates} alt="Emperor Haile Selassie I standing with university graduates in caps and gowns" className="relative w-full object-cover" loading="lazy" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-ivory">Education as his enduring legacy.</h2>
          <p className="mt-6 text-lg leading-relaxed text-ivory2">
            Among his most cherished causes was the advancement of higher education — celebrating scholars and
            opening the doors of the university to a new generation. It is this part of his legacy that the
            Emperor Haile Selassie I Memorial Foundation, and its Friends in America, work to honor today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <GoldBtn onClick={() => openDonate()} testid="legacy-donate"><Heart className="h-5 w-5" /> Honor His Legacy</GoldBtn>
            <OutlineBtn to="/ethiopia" testid="legacy-work">Our Work Today <ArrowRight className="h-4 w-4" /></OutlineBtn>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
